import React, { useState, useRef } from 'react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../../lib/utils';

interface FileUploadZoneProps {
  onFilesAdded: (files: File[]) => void;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ onFilesAdded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'message/rfc822',
    'application/vnd.ms-outlook',
    'image/jpeg',
    'image/png',
    'text/plain',
  ];

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFiles = (files: FileList): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} is too large (max 10MB)`);
      } else if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.txt')) {
        errors.push(`${file.name} is not a supported file type`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    return validFiles;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        onFilesAdded(validFiles);
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        onFilesAdded(validFiles);
      }
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer",
        isDragging
          ? "border-primary bg-primary/5"
          : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleBrowseClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,.eml,.msg,.jpg,.jpeg,.png"
        onChange={handleFileInputChange}
        className="hidden"
      />

      <div className="flex flex-col items-center text-center">
        <ArrowUpTrayIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <p className="text-sm font-medium text-foreground mb-1">
          Drop files here or{' '}
          <span className="text-primary hover:underline">browse</span>
        </p>
        <p className="text-xs text-muted-foreground">
          PDF, Word, Text, Email, or Image files (max 10MB each)
        </p>
      </div>
    </div>
  );
};

export default FileUploadZone;
